import React, { useState, useEffect } from 'react';
import { Card, Spin, Table, Input, Button, DatePicker, Radio, Select, Drawer, Space } from 'antd';
import {
  ShoppingCartOutlined,
  ReloadOutlined,
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReportService from '../../../services/ReportService';
import { getUpdatedMobileDateRange } from '../../../helpers/mobileDateRange';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TopProductsReports = () => {
  const [loading, setLoading] = useState(true);
  const [dateFilterType, setDateFilterType] = useState('quick');
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [orderBy, setOrderBy] = useState('DESC');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftDateFilterType, setDraftDateFilterType] = useState('quick');
  const [draftDateFilter, setDraftDateFilter] = useState('today');
  const [draftSelectedDate, setDraftSelectedDate] = useState(dayjs());
  const [draftDateRange, setDraftDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [draftSelectedBranch, setDraftSelectedBranch] = useState(null);
  const [draftOrderBy, setDraftOrderBy] = useState('DESC');

  const [topProducts, setTopProducts] = useState([]);
  const [topProductsLoading, setTopProductsLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const token = localStorage.getItem("token") || localStorage.getItem("x-access-token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const companyCode = userDetails?.CompanyCode || null;

  useEffect(() => {
    const loadBranches = async () => {
      if (!companyCode) {
        setBranches([]);
        return;
      }
      try {
        const response = await ReportService.company.getBranches({
          token,
          CompanyCode: companyCode
        });
        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
          setBranches(data);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
        setBranches([]);
      }
    };
    loadBranches();
  }, [token, companyCode]);

  const getDateParams = (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null) => {
    const currentFilter = filterOverride !== null ? filterOverride : dateFilter;
    const currentFilterType = filterTypeOverride !== null ? filterTypeOverride : dateFilterType;
    const currentDateRange = dateRangeOverride !== null ? dateRangeOverride : dateRange;
    const currentSelectedDate = selectedDateOverride !== null ? selectedDateOverride : selectedDate;

    if (currentFilterType === 'range' && currentDateRange && currentDateRange[0] && currentDateRange[1]) {
      return {
        StartDate: currentDateRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentDateRange[1].add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    if (currentFilterType === 'quick' && currentFilter === 'custom') {
      return {
        StartDate: currentSelectedDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentSelectedDate.add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    return {
      DateFilter: currentFilter
    };
  };

  const fetchTopProducts = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, orderByOverride = null, branchCodeOverride = null) => {
    try {
      setTopProductsLoading(true);

      if (!companyCode) {
        console.error('CompanyCode is required');
        setTopProducts([]);
        return;
      }

      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const currentOrderBy = orderByOverride !== null ? orderByOverride : orderBy;
      const response = await ReportService.sales.getBranchTopProductsReport({
        token,
        CompanyCode: companyCode,
        BranchCode: branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || undefined),
        OrderBy: currentOrderBy,
        ...dateParams
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
        setTopProducts(data);
      } else {
        setTopProducts([]);
      }
    } catch (error) {
      console.error('Error fetching top products:', error);
      setTopProducts([]);
    } finally {
      setTopProductsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchTopProducts();
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const openFilters = () => {
    setDraftDateFilterType(dateFilterType);
    setDraftDateFilter(dateFilter);
    setDraftSelectedDate(selectedDate);
    setDraftDateRange(dateRange);
    setDraftSelectedBranch(selectedBranch);
    setDraftOrderBy(orderBy);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setDateFilterType(draftDateFilterType);
    setDateFilter(draftDateFilter);
    setSelectedDate(draftSelectedDate);
    setDateRange(draftDateRange);
    setSelectedBranch(draftSelectedBranch);
    setOrderBy(draftOrderBy);
    setIsFilterOpen(false);
    if (draftDateFilterType === 'range') {
      fetchTopProducts(null, 'range', draftDateRange, null, draftOrderBy, draftSelectedBranch);
    } else if (draftDateFilter === 'custom') {
      fetchTopProducts('custom', 'quick', null, draftSelectedDate, draftOrderBy, draftSelectedBranch);
    } else {
      fetchTopProducts(draftDateFilter, 'quick', null, null, draftOrderBy, draftSelectedBranch);
    }
  };

  const clearFilters = () => {
    const defaultDateFilterType = 'quick';
    const defaultDateFilter = 'today';
    const defaultSelectedDate = dayjs();
    const defaultDateRange = [dayjs().subtract(7, 'day'), dayjs()];
    const defaultSelectedBranch = null;
    const defaultOrderBy = 'DESC';

    setDraftDateFilterType(defaultDateFilterType);
    setDraftDateFilter(defaultDateFilter);
    setDraftSelectedDate(defaultSelectedDate);
    setDraftDateRange(defaultDateRange);
    setDraftSelectedBranch(defaultSelectedBranch);
    setDraftOrderBy(defaultOrderBy);

    setDateFilterType(defaultDateFilterType);
    setDateFilter(defaultDateFilter);
    setSelectedDate(defaultSelectedDate);
    setDateRange(defaultDateRange);
    setSelectedBranch(defaultSelectedBranch);
    setOrderBy(defaultOrderBy);
    fetchTopProducts(defaultDateFilter, defaultDateFilterType, null, null, defaultOrderBy, null);
  };

  const handleMobileDraftRangeDateChange = (index, date) => {
    const nextRange = getUpdatedMobileDateRange(draftDateRange, index, date, 7);
    if (!nextRange) return;
    setDraftDateRange(nextRange);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const calculateTotalQuantity = (data) => {
    return (data || [])
      .filter(row => row.IsChild !== true)
      .reduce((sum, row) => sum + parseFloat(row.ToplamMiktar || 0), 0);
  };

  const calculateTotalSales = (data) => {
    return (data || [])
      .filter(row => row.IsChild !== true)
      .reduce((sum, row) => sum + parseFloat(row.ToplamSatis || 0), 0);
  };

  const groupedData = topProducts.reduce((acc, item) => {
    const key = item.SubeKodu;
    if (!acc[key]) {
      acc[key] = {
        SubeKodu: item.SubeKodu,
        SubeAdi: item.SubeAdi,
        products: []
      };
    }
    acc[key].products.push(item);
    return acc;
  }, {});

  const topProductsColumns = [
    {
      title: <span className={isMobile ? 'text-sm' : 'text-base'}>Ürün Adı</span>,
      dataIndex: 'UrunAdi',
      key: 'UrunAdi',
      width: isMobile ? '44%' : '30%',
      render: (text, record) => (
        <span className={isMobile ? 'text-sm' : 'text-base'}>
          {text}
          {record.PorsiyonAdi && <span className={isMobile ? 'text-gray-500 text-xs' : 'text-gray-500 text-sm'}> ({record.PorsiyonAdi})</span>}
        </span>
      ),
      ellipsis: isMobile
    },
    {
      title: <span className={isMobile ? 'text-sm' : 'text-base'}>Toplam Miktar</span>,
      dataIndex: 'ToplamMiktar',
      key: 'ToplamMiktar',
      width: isMobile ? '28%' : '20%',
      render: (value) => (
        <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold text-blue-600`}>
          {formatNumber(value || 0)}
        </span>
      ),
      sorter: (a, b) => (a.ToplamMiktar || 0) - (b.ToplamMiktar || 0),
    },
    {
      title: <span className={isMobile ? 'text-sm' : 'text-base'}>Toplam Satış</span>,
      dataIndex: 'ToplamSatis',
      key: 'ToplamSatis',
      width: isMobile ? '28%' : '20%',
      render: (value) => (
        <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold text-green-600`}>
          {formatNumber(value || 0)} ₺
        </span>
      ),
      sorter: (a, b) => (a.ToplamSatis || 0) - (b.ToplamSatis || 0),
    },
  ];

  const filterData = (data) => {
    if (!searchText) return data;
    const needle = searchText.toLowerCase();
    return data.filter(row =>
      String(row.SubeAdi || '').toLowerCase().includes(needle) ||
      String(row.UrunAdi || '').toLowerCase().includes(needle) ||
      String(row.PorsiyonAdi || '').toLowerCase().includes(needle)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm mb-4">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg">
              <ShoppingCartOutlined className="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                En Çok/Az Satılan Ürünler
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Şube bazlı ürün satış raporları
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchTopProducts(dateFilter, dateFilterType, dateRange, selectedDate, orderBy, selectedBranch)}
              loading={topProductsLoading}
            >
              Yenile
            </Button>
            <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <Input
          allowClear
          placeholder="Şube, ürün veya porsiyon ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="large"
          className="flex-1 rounded-lg"
        />
      </div>

      {/* Products Report */}
      {Object.values(groupedData).map((branch) => {
        const filteredProducts = filterData(branch.products);
        const totalQuantity = calculateTotalQuantity(filteredProducts);
        const totalSales = calculateTotalSales(filteredProducts);

        return (
          <Card
            key={branch.SubeKodu}
            title={
              <div className="flex items-center gap-2">
                <ShoppingCartOutlined className="text-blue-500" />
                <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
                  {branch.SubeAdi}
                </h2>
              </div>
            }
            bordered={false}
            className="shadow-lg rounded-2xl border border-gray-100 bg-white mb-4"
            styles={{ body: { padding: isMobile ? 12 : 18 } }}
          >
            <Table
              columns={topProductsColumns}
              dataSource={filteredProducts}
              loading={topProductsLoading}
              rowKey={(record, index) => `${record.SubeKodu}-${record.UrunAdi}-${record.PorsiyonAdi}-${index}`}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Toplam ${total} kayıt`,
              }}
              size={isMobile ? 'small' : 'middle'}
              summary={(pageData) => {
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row className="bg-gray-50">
                      <Table.Summary.Cell index={0}>
                        <span className="font-bold text-base">Toplam</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <span className="font-bold text-base text-blue-600">{formatNumber(totalQuantity)}</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        <span className="font-bold text-base text-green-600">{formatNumber(totalSales)} ₺</span>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        );
      })}

      {/* General Total */}
      {Object.keys(groupedData).length > 0 && (
        <Card
          bordered={false}
          className="shadow-lg rounded-2xl border border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50"
          styles={{ body: { padding: isMobile ? 12 : 18 } }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-800">Genel Toplam</h3>
              <p className="text-sm text-gray-600">Tüm şubeler için toplam</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="text-center sm:text-right">
                <p className="text-sm text-gray-600">Toplam Miktar</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatNumber(calculateTotalQuantity(filterData(topProducts)))}
                </p>
              </div>
              <div className="text-center sm:text-right">
                <p className="text-sm text-gray-600">Toplam Satış</p>
                <p className="text-xl font-bold text-green-600">
                  {formatNumber(calculateTotalSales(filterData(topProducts)))} ₺
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Sıralama</span>
            <Radio.Group
              value={draftOrderBy}
              onChange={(e) => setDraftOrderBy(e.target.value)}
              className="w-full"
            >
              <Radio.Button value="DESC" className="w-1/2 text-center">
                <ArrowUpOutlined /> En Çok
              </Radio.Button>
              <Radio.Button value="ASC" className="w-1/2 text-center">
                <ArrowDownOutlined /> En Az
              </Radio.Button>
            </Radio.Group>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Filtre Tipi</span>
            <Radio.Group
              value={draftDateFilterType}
              onChange={(e) => setDraftDateFilterType(e.target.value)}
              className="w-full"
            >
              <Radio.Button value="quick" className="w-1/2 text-center">Gün</Radio.Button>
              <Radio.Button value="range" className="w-1/2 text-center">Tarih Aralığı</Radio.Button>
            </Radio.Group>
          </div>

          {draftDateFilterType === 'quick' ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Gün Seçimi</span>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Bugün', value: 'today' },
                    { label: 'Dün', value: 'yesterday' },
                    { label: 'Gün Seçin', value: 'custom' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setDraftDateFilter(option.value)}
                      className={`
                        px-3 py-2 text-sm font-semibold transition-all duration-300 ease-out rounded-lg
                        ${draftDateFilter === option.value
                          ? 'bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-gray-100 border border-gray-200'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              {draftDateFilter === 'custom' && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-slate-500">Özel Gün</span>
                  <DatePicker
                    value={draftSelectedDate}
                    onChange={(date) => setDraftSelectedDate(date)}
                    format="DD.MM.YYYY"
                    className="w-full"
                    placeholder="Gün seçin"
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">Tarih Aralığı</span>
              {isMobile ? (
                <div className="flex items-center gap-1 w-full">
                  <DatePicker
                    value={draftDateRange?.[0]}
                    onChange={(date) => handleMobileDraftRangeDateChange(0, date)}
                    format="DD.MM.YYYY"
                    placeholder="Başlangıç"
                    className="w-full"
                    size="small"
                    allowClear={false}
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                  <span className="text-slate-400 text-xs">-</span>
                  <DatePicker
                    value={draftDateRange?.[1]}
                    onChange={(date) => handleMobileDraftRangeDateChange(1, date)}
                    format="DD.MM.YYYY"
                    placeholder="Bitiş"
                    className="w-full"
                    size="small"
                    allowClear={false}
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                </div>
              ) : (
                <RangePicker
                  value={draftDateRange}
                  onChange={(dates) => setDraftDateRange(dates || [])}
                  format="DD.MM.YYYY"
                  className="w-full"
                  placement="bottomLeft"
                  getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                />
              )}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Şube</span>
            <Select
              value={draftSelectedBranch}
              onChange={(value) => setDraftSelectedBranch(value)}
              placeholder="Şube seçiniz"
              className="w-full"
              allowClear
            >
              {branches.map((branch) => (
                <Select.Option key={branch.SubeKodu} value={branch.SubeKodu}>
                  {branch.SubeAdi}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default TopProductsReports;

